import { useEffect, useLayoutEffect, useRef } from "react";

const DEFAULT_FLIP_DURATION_MS = 900;
const DEFAULT_GLOW_FADE_MS = 700;

/**
 * Deltas above this (in pixels) are treated as layout jumps (resize, filter
 * switch, big re-fetch) rather than rank shuffles — we skip animating them
 * so the user doesn't see a dramatic sweep.
 */
const MAX_ANIMATED_DELTA = 600;

const STYLE_ID = "leaderboard-flip-keyframes";
const KEYFRAMES_CSS = `
@keyframes leaderboard-fade-in {
  0%   { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes leaderboard-shine {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}`;

interface MedalShineColors {
  bg: string;
  glow: string;
}

function defaultGetMedalShineColors(rank: number): MedalShineColors {
  switch (rank) {
    case 1:
      return {
        bg: "color-mix(in srgb, var(--color-medal-gold) 70%, transparent)",
        glow: "color-mix(in srgb, var(--color-medal-gold) 45%, transparent)",
      };
    case 2:
      return {
        bg: "color-mix(in srgb, var(--color-medal-silver) 70%, transparent)",
        glow: "color-mix(in srgb, var(--color-medal-silver) 45%, transparent)",
      };
    case 3:
      return {
        bg: "color-mix(in srgb, var(--color-medal-bronze) 70%, transparent)",
        glow: "color-mix(in srgb, var(--color-medal-bronze) 45%, transparent)",
      };
    default:
      return {
        bg: "color-mix(in srgb, #3B82F6 18%, transparent)",
        glow: "color-mix(in srgb, #3B82F6 35%, transparent)",
      };
  }
}

interface LeaderboardEntry {
  id: string;
  value: number;
  rank: number;
}

interface UseLeaderboardFlipAnimationOptions {
  entries: LeaderboardEntry[];
  rowRefs: React.RefObject<Map<string, HTMLDivElement>>;
  containerRef: React.RefObject<HTMLElement | null>;
  /**
   * Scrollable element containing the rows. Positions are measured relative
   * to its scroll content (not its viewport) so user-initiated scrolling
   * never registers as row movement.
   */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  getMedalShineColors?: (rank: number) => MedalShineColors;
  flipDurationMs?: number;
  glowFadeMs?: number;
}

interface MovedRow {
  el: HTMLDivElement;
  delta: number;
  isActive: boolean;
  rank: number;
}

/**
 * FLIP animation for ranked leaderboards.
 *
 * Plays a tight translate + shine when a row's rank changes, and a subtle
 * fade-in for rows that appear. Positions are measured inside the scroll
 * content's own coordinate space, so the user's scroll position is never
 * conflated with row movement.
 */
export function useLeaderboardFlipAnimation({
  entries,
  rowRefs,
  containerRef,
  scrollContainerRef,
  getMedalShineColors = defaultGetMedalShineColors,
  flipDurationMs = DEFAULT_FLIP_DURATION_MS,
  glowFadeMs = DEFAULT_GLOW_FADE_MS,
}: UseLeaderboardFlipAnimationOptions) {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = KEYFRAMES_CSS;
    document.head.appendChild(style);
  }, []);

  const prevPositions = useRef<Map<string, number>>(new Map());
  const prevValues = useRef<Map<string, number>>(new Map());
  const knownIds = useRef<Set<string>>(new Set());
  const isInitialRender = useRef(true);

  useLayoutEffect(() => {
    // Measure row positions relative to the scroll content's internal
    // coordinate space — this stays stable when the user scrolls, so manual
    // scrolling never triggers a false FLIP (which previously made all rows
    // appear to "scroll up" on the next data refresh).
    const scrollEl = scrollContainerRef?.current ?? null;
    const fallbackTop = containerRef.current?.getBoundingClientRect().top ?? 0;

    const measureTop = (el: HTMLElement): number => {
      if (scrollEl) {
        const scrollRect = scrollEl.getBoundingClientRect();
        return el.getBoundingClientRect().top - scrollRect.top + scrollEl.scrollTop;
      }
      return el.getBoundingClientRect().top - fallbackTop;
    };

    const currentPositions = new Map<string, number>();
    for (const [id, el] of rowRefs.current) {
      currentPositions.set(id, measureTop(el));
    }

    const currentValues = new Map<string, number>();
    for (const entry of entries) {
      currentValues.set(entry.id, entry.value);
    }

    // Seed on first render (or when we lost all prior state) — no animation.
    if (isInitialRender.current || prevPositions.current.size === 0) {
      isInitialRender.current = false;
      prevPositions.current = currentPositions;
      prevValues.current = currentValues;
      for (const entry of entries) knownIds.current.add(entry.id);
      return;
    }

    // Which rows had their underlying value change?
    const changedIds = new Set<string>();
    for (const [id, value] of currentValues) {
      const prev = prevValues.current.get(id);
      if (prev == null || prev !== value) changedIds.add(id);
    }

    const rankMap = new Map<string, number>();
    for (const entry of entries) rankMap.set(entry.id, entry.rank);

    const movedRows: MovedRow[] = [];
    let skipLargeJump = false;

    for (const [id, el] of rowRefs.current) {
      const prevTop = prevPositions.current.get(id);
      const currentTop = currentPositions.get(id);

      if (prevTop == null) {
        // New row — gentle fade-in from below.
        if (!knownIds.current.has(id)) {
          knownIds.current.add(id);
          el.style.animation = `leaderboard-fade-in ${flipDurationMs}ms cubic-bezier(0.22, 1, 0.36, 1) both`;
          const clearFade = () => {
            el.style.animation = "";
            el.removeEventListener("animationend", clearFade);
          };
          el.addEventListener("animationend", clearFade);
        }
        continue;
      }

      if (currentTop == null) continue;

      const delta = prevTop - currentTop;
      if (Math.abs(delta) < 1) continue;

      // Guard against layout jumps (filter changes, container resizes, tab
      // switches) that would otherwise manifest as a dramatic mass shuffle.
      if (Math.abs(delta) > MAX_ANIMATED_DELTA) {
        skipLargeJump = true;
        break;
      }

      movedRows.push({
        el,
        delta,
        isActive: changedIds.has(id),
        rank: rankMap.get(id) ?? 999,
      });
    }

    if (skipLargeJump || movedRows.length === 0) {
      prevPositions.current = currentPositions;
      prevValues.current = currentValues;
      return;
    }

    // ---- FLIP invert ----
    for (const { el, delta, isActive, rank } of movedRows) {
      el.style.transition = "none";
      el.style.transform = `translateY(${delta}px)`;
      if (isActive) {
        const { bg, glow } = getMedalShineColors(rank);
        el.style.zIndex = "10";
        el.style.background = `linear-gradient(90deg, ${bg} 0%, ${bg} 35%, rgba(255,255,255,0.14) 50%, ${bg} 65%, ${bg} 100%)`;
        el.style.backgroundSize = "200% 100%";
        el.style.animation = `leaderboard-shine ${flipDurationMs}ms ease-in-out`;
        el.style.boxShadow = `0 0 18px 2px ${glow}`;
      }
    }

    // Force reflow so the browser registers the inverted state.
    containerRef.current?.getBoundingClientRect();

    // ---- Play ----
    for (const { el, isActive, rank } of movedRows) {
      el.style.transition = `transform ${flipDurationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`;
      el.style.transform = "";

      const cleanup = () => {
        el.removeEventListener("transitionend", cleanup);
        el.style.transform = "";
        el.style.animation = "";

        if (isActive) {
          const { bg } = getMedalShineColors(rank);
          el.style.background = "";
          el.style.backgroundSize = "";
          el.style.backgroundColor = bg;
          // Force reflow so the browser registers the starting color.
          el.getBoundingClientRect();
          el.style.transition = `box-shadow ${glowFadeMs}ms ease-out, background-color ${glowFadeMs}ms ease-out`;
          el.style.boxShadow = "0 0 0 0 transparent";
          el.style.backgroundColor = "transparent";
          setTimeout(() => {
            el.style.transition = "";
            el.style.zIndex = "";
            el.style.boxShadow = "";
            el.style.backgroundColor = "";
          }, glowFadeMs);
        } else {
          el.style.transition = "";
          el.style.zIndex = "";
          el.style.background = "";
          el.style.backgroundSize = "";
          el.style.boxShadow = "";
        }
      };
      el.addEventListener("transitionend", cleanup);
    }

    prevPositions.current = currentPositions;
    prevValues.current = currentValues;
  }, [entries, rowRefs, containerRef, scrollContainerRef, getMedalShineColors, flipDurationMs, glowFadeMs]);
}
